import React from 'react';
import { Box, Container, Grid, Stack } from '@mui/material';
import { useThemeContext } from '../../hook/useThemeManager';
import { CoverImageRenderer, ICoverImageRenderer } from './CoverImageRenderer';
import { AuditionMetaBento } from './AuditionMetaBento';
import { AuditionSuggestion, IAuditionSuggestion } from './AuditionSuggestion';
import RoleRenderer from './RoleRenderer';
import { AuditionDescription } from './AuditionDescription';
import AttachmentRenderer from './AttachmentRenderer';
import { CompanyCard } from '../Cards/CompanyCard';
import { IRole } from '../../types/global/helper';

export interface IAuditionWrapper extends ICoverImageRenderer, IAuditionSuggestion {
    /** Drawer / modal: hero is full-bleed horizontally; content below is padded. */
    embeddedInDrawer?: boolean;
    onApplyClick?: (role: IRole) => void;
    isManageMode?: boolean;
    onManageClick?: (role: IRole) => void;
}

const AuditionWrapper: React.FC<IAuditionWrapper> = props => {
    const { device } = useThemeContext();
    const {
        embeddedInDrawer,
        audition,
        editMode,
        onImageChange,
        disableSuggestion,
        similarAuditions,
        hideDetails,
        onApplyClick,
        isManageMode,
        onManageClick,
        onBackButtonPressed,
    } = props;

    const contentPadding = { xs: 2, sm: 3 };
    /** Page only: inset below hero (hero is a sibling — never add padding on its wrapper). */
    const pageContentPx = { xs: 3, sm: 3.5, md: 4, lg: 5, xl: 5 };
    const maxContent = 1120;

    return (
        <Container
            disableGutters
            maxWidth={false}
            sx={{
                height: {
                    xs: '100dvh',
                    sm: '100%',
                },
                px: 0,
                width: '100%',
            }}>
            <Stack width="100%" alignItems="stretch" sx={{ mx: 0 }}>
                <CoverImageRenderer
                    audition={audition}
                    editMode={editMode}
                    onImageChange={onImageChange}
                    immersive
                    onBackButtonPressed={embeddedInDrawer ? undefined : onBackButtonPressed}
                />

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: embeddedInDrawer ? 'none' : maxContent,
                        mx: embeddedInDrawer ? 0 : 'auto',
                        px: embeddedInDrawer ? contentPadding : pageContentPx,
                        pt: { xs: 2, sm: 3 },
                        pb: device.isMobile
                            ? {
                                  xs: 'calc(env(safe-area-inset-bottom, 0px) + min(240px, 40dvh))',
                              }
                            : { xs: 2, sm: 3 },
                        boxSizing: 'border-box',
                        minWidth: 0,
                    }}>
                    <Grid container spacing={3} sx={{ minWidth: 0 }}>
                        <Grid item xs={12} lg={8} sx={{ minWidth: 0 }}>
                            <Stack spacing={3}>
                                <AuditionDescription audition={audition} />
                                <AuditionMetaBento audition={audition} />
                                <RoleRenderer
                                    audition={audition}
                                    onApplyClick={onApplyClick}
                                    isManageMode={isManageMode}
                                    onManageClick={onManageClick}
                                />
                                <AttachmentRenderer audition={audition} />
                                {!device.isDesktop &&
                                    audition.production_company &&
                                    audition.production_company._id && (
                                        <CompanyCard data={audition.production_company} enableHeader />
                                    )}
                                {device.isTablet && (
                                    <AuditionSuggestion
                                        audition={audition}
                                        disableSuggestion={disableSuggestion}
                                        similarAuditions={similarAuditions}
                                        hideDetails={hideDetails}
                                        onApplyClick={onApplyClick}
                                    />
                                )}
                            </Stack>
                        </Grid>
                        {device.isDesktop && (
                            <Grid item xs={12} lg={4}>
                                <Box
                                    sx={{
                                        position: 'sticky',
                                        top: 24,
                                        alignSelf: 'flex-start',
                                    }}>
                                    <AuditionSuggestion
                                        audition={audition}
                                        disableSuggestion={disableSuggestion}
                                        similarAuditions={similarAuditions}
                                        hideDetails={hideDetails}
                                        onApplyClick={onApplyClick}
                                    />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            </Stack>
            {device.isMobile && (
                <Stack
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        width: '100%',
                        maxWidth: '100vw',
                        zIndex: 10,
                        px: 0,
                        pt: 0,
                        pb: 'env(safe-area-inset-bottom, 0px)',
                        boxSizing: 'border-box',
                        justifyContent: 'flex-end',
                        pointerEvents: 'none',
                        '& > *': { pointerEvents: 'auto' },
                    }}>
                    <AuditionSuggestion
                        audition={audition}
                        disableSuggestion
                        similarAuditions={similarAuditions}
                        hideDetails={hideDetails}
                        onApplyClick={onApplyClick}
                        mobileDocked
                    />
                </Stack>
            )}
        </Container>
    );
};

export { AuditionWrapper };
